import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, FileSpreadsheet } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Loading from '../../common/Loading';
import { fieldAPI } from '../../../api/endpoints';
import { getActiveUniqueFields } from '../../../utils/fieldHelpers';
import { generateCSV, downloadCSV } from '../../../utils/csvParser';
// Removed unused attendee helper imports
import { useToastContext } from '../../../context/ToastContext';

const ExportConfigModal = ({ isOpen, onClose, selectedEvent, attendees, onExportComplete }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [selectedFields, setSelectedFields] = useState([]);
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToastContext();

  // Fetch custom fields for the event
  const { data: fieldsData, isLoading: fieldsLoading } = useQuery({
    queryKey: ['custom-fields', selectedEvent],
    queryFn: () => fieldAPI.getAll(selectedEvent),
    enabled: !!selectedEvent && isOpen,
  });

  const customFields = React.useMemo(() => {
    return fieldsData ? getActiveUniqueFields(fieldsData.data) : [];
  }, [fieldsData]);

  // Initialize selected fields when custom fields load
  React.useEffect(() => {
    if (customFields.length > 0 && selectedFields.length === 0) {
      // Select all fields by default
      setSelectedFields(customFields.map(field => field.field_name));
    }
  }, [customFields, selectedFields.length]);

  const handleFieldToggle = (fieldName) => {
    setSelectedFields(prev => 
      prev.includes(fieldName)
        ? prev.filter(name => name !== fieldName)
        : [...prev, fieldName]
    );
  };

  const handleSelectAll = () => {
    setSelectedFields(customFields.map(field => field.field_name));
  };

  const handleSelectNone = () => {
    setSelectedFields([]);
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to export');
      return;
    }

    setIsExporting(true);

    try {
      // For advanced export, we'll use the backend API which includes all fields properly
      // The backend export already respects event field settings and includes all data
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/events/${selectedEvent}/attendees/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      // Get the CSV content
      const csvText = await response.text();
      
      // Parse the CSV to filter only selected fields
      const lines = csvText.split('\n');
      if (lines.length < 2) {
        throw new Error('No data received from server');
      }

      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      
      // Find indices of selected fields (match by field labels)
      const selectedIndices = [];
      const filteredHeaders = [];
      
      selectedFields.forEach(fieldName => {
        const field = customFields.find(f => f.field_name === fieldName);
        if (field) {
          const headerIndex = headers.findIndex(h => 
            h.toLowerCase() === field.field_label.toLowerCase() ||
            h.toLowerCase() === fieldName.toLowerCase()
          );
          if (headerIndex !== -1) {
            selectedIndices.push(headerIndex);
            filteredHeaders.push(headers[headerIndex]);
          }
        }
      });

      // Always include system fields (Badge Number, Check-in Status, Registration Date)
      const systemFields = ['Badge Number', 'Check-in Status', 'Registration Date'];
      systemFields.forEach(sysField => {
        const sysIndex = headers.findIndex(h => h.toLowerCase().includes(sysField.toLowerCase()));
        if (sysIndex !== -1 && !selectedIndices.includes(sysIndex)) {
          selectedIndices.push(sysIndex);
          filteredHeaders.push(headers[sysIndex]);
        }
      });

      // Build filtered CSV
      let filteredCSV = filteredHeaders.map(h => `"${h}"`).join(',') + '\n';
      
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',');
          const filteredValues = selectedIndices.map(idx => values[idx] || '""');
          filteredCSV += filteredValues.join(',') + '\n';
        }
      }

      // Download the filtered CSV
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `attendees-advanced-${selectedEvent}-${timestamp}.csv`;
      
      const blob = new Blob([filteredCSV], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (exportFormat === 'xlsx') {
        toast.info('Excel export generated as CSV format which Excel can open directly');
      }

      onExportComplete({
        success: true,
        count: attendees.length,
        format: exportFormat.toUpperCase(),
        fieldsCount: selectedFields.length
      });

    } catch (error) {
      console.error('Advanced export error:', error);
      toast.error('Failed to export attendees: ' + error.message);
      
      // Fallback to client-side export
      try {
        const fieldsToExport = customFields.filter(field => 
          selectedFields.includes(field.field_name)
        );

        const timestamp = new Date().toISOString().split('T')[0];
        const baseFilename = `attendees-advanced-fallback-${selectedEvent}-${timestamp}`;
        
        const csvContent = generateCSV(attendees, fieldsToExport);
        const filename = `${baseFilename}.csv`;
        downloadCSV(csvContent, filename);
        
        toast.success('Export completed using fallback method');
        
        onExportComplete({
          success: true,
          count: attendees.length,
          format: exportFormat.toUpperCase(),
          fieldsCount: fieldsToExport.length
        });
      } catch (fallbackError) {
        console.error('Fallback export error:', fallbackError);
        toast.error('Both backend and fallback export methods failed');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFields([]);
    onClose();
  };

  if (fieldsLoading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <Modal.Content>
          <Loading text="Loading export options..." />
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        <h2 className="text-xl font-semibold text-gray-900">Export Configuration</h2>
        <p className="text-sm text-gray-600 mt-1">
          Configure your export settings and select which fields to include
        </p>
      </Modal.Header>

      <Modal.Content>
        {/* Export Summary */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{attendees.length}</div>
            <div className="text-sm text-blue-800">Attendees</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{selectedFields.length}</div>
            <div className="text-sm text-green-800">Selected Fields</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{customFields.length}</div>
            <div className="text-sm text-purple-800">Available Fields</div>
          </div>
        </div>

        {/* Export Format */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Export Format</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setExportFormat('csv')}
              className={`flex items-center gap-3 p-4 border rounded-lg transition-colors ${
                exportFormat === 'csv'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <FileText className="h-6 w-6" />
              <div className="text-left">
                <div className="font-medium">CSV File</div>
                <div className="text-sm text-gray-500">Comma-separated values</div>
              </div>
            </button>
            <button
              onClick={() => setExportFormat('xlsx')}
              className={`flex items-center gap-3 p-4 border rounded-lg transition-colors ${
                exportFormat === 'xlsx'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <FileSpreadsheet className="h-6 w-6" />
              <div className="text-left">
                <div className="font-medium">Excel File</div>
                <div className="text-sm text-gray-500">Microsoft Excel format</div>
              </div>
            </button>
          </div>
        </div>

        {/* Export Options */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Export Options</label>
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeHeaders}
                onChange={(e) => setIncludeHeaders(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Include column headers</span>
            </label>
          </div>
        </div>

        {/* Field Selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Select Fields to Export
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleSelectAll}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={handleSelectNone}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Select None
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
            {customFields.map((field, index) => (
              <label
                key={field.field_name}
                className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer ${
                  index !== customFields.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedFields.includes(field.field_name)}
                  onChange={() => handleFieldToggle(field.field_name)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {field.field_label}
                    {field.is_required && (
                      <span className="ml-1 text-xs text-red-600">(Required)</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {field.field_type} • {field.field_name}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {selectedFields.length === 0 && (
            <p className="text-sm text-red-600 mt-2">
              Please select at least one field to export
            </p>
          )}
        </div>

        {/* Preview */}
        {selectedFields.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Export Preview</h4>
            <p className="text-sm text-gray-600">
              Your export will include {attendees.length} attendees with the following {selectedFields.length} fields:
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {customFields
                .filter(field => selectedFields.includes(field.field_name))
                .map(field => (
                  <span
                    key={field.field_name}
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary-100 text-primary-700"
                  >
                    {field.field_label}
                  </span>
                ))}
            </div>
          </div>
        )}
      </Modal.Content>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          disabled={selectedFields.length === 0 || isExporting}
          loading={isExporting}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          Export {exportFormat.toUpperCase()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportConfigModal;
